import {
  Archive,
  CheckCircle2,
  Info,
  type LucideIcon,
  OctagonAlert,
  TriangleAlert,
  X,
} from "lucide-react";
import { type CSSProperties, type ReactNode } from "react";
import { Toaster as SonnerToaster, type ToasterProps } from "sonner";

import { cn } from "@/lib/utils/cn";

const Toaster = (props: ToasterProps) => (
  <SonnerToaster
    position="top-right"
    duration={3000}
    visibleToasts={5}
    closeButton={false}
    expand
    toastOptions={{ unstyled: true }}
    {...props}
  />
);

export type ToastType =
  | "neutral"
  | "error"
  | "warning"
  | "success"
  | "info"
  | "archive";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastStyle {
  style: CSSProperties;
  icon: LucideIcon;
}

const toastStyles: Record<ToastType, ToastStyle> = {
  success: {
    style: {
      background: "var(--badge-success-bg)",
      color: "var(--badge-success-text)",
      borderColor: "var(--badge-success-border)",
    },
    icon: CheckCircle2,
  },
  error: {
    style: {
      background: "var(--badge-error-bg)",
      color: "var(--badge-error-text)",
      borderColor: "var(--badge-error-border)",
    },
    icon: OctagonAlert,
  },
  warning: {
    style: {
      background: "var(--badge-warning-bg)",
      color: "var(--badge-warning-text)",
      borderColor: "var(--badge-warning-border)",
    },
    icon: TriangleAlert,
  },
  info: {
    style: {
      background: "var(--badge-info-bg)",
      color: "var(--badge-info-text)",
      borderColor: "var(--badge-info-border)",
    },
    icon: Info,
  },
  neutral: {
    style: {
      background: "var(--badge-neutral-bg)",
      color: "var(--badge-neutral-text)",
      borderColor: "var(--badge-neutral-border)",
    },
    icon: Info,
  },
  archive: {
    style: {
      background: "var(--badge-archive-bg)",
      color: "var(--badge-archive-text)",
      borderColor: "var(--badge-archive-border)",
    },
    icon: Archive,
  },
};

interface ToastBodyProps {
  type: ToastType;
  message: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  action?: ToastAction;
  icon?: LucideIcon;
  spin?: boolean;
  onDismiss?: () => void;
}

const ToastBody = ({
  type,
  message,
  title,
  description,
  action,
  icon,
  spin,
  onDismiss,
}: ToastBodyProps) => {
  const { style, icon: defaultIcon } = toastStyles[type];
  const Icon = icon ?? defaultIcon;
  const hasExtras = Boolean(title || description || action);
  return (
    <div
      data-slot="toast"
      role="status"
      aria-live="polite"
      style={style}
      className={cn(
        "pointer-events-auto flex w-full min-w-[320px] max-w-[480px] gap-3",
        "rounded-xl border px-4 py-3 shadow-lg backdrop-blur-sm",
        hasExtras ? "items-start" : "items-center",
      )}
    >
      <Icon
        className={cn(
          "size-5 shrink-0",
          hasExtras && "mt-px",
          spin && "animate-spin",
        )}
        aria-hidden="true"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {title ? (
          <span className="text-sm font-semibold leading-5">{title}</span>
        ) : null}
        <span className="text-sm leading-5">{message}</span>
        {description ? (
          <span className="mt-1 text-xs leading-5 opacity-80">
            {description}
          </span>
        ) : null}
        {action ? (
          <button
            type="button"
            onClick={action.onClick}
            className={cn(
              "mt-2 self-start rounded-md border border-current/40 px-2.5 py-1",
              "text-xs font-medium transition-colors duration-200",
              "hover:bg-current/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-current",
            )}
          >
            {action.label}
          </button>
        ) : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className={cn(
            "-mr-1 shrink-0 rounded-md p-1 transition-colors duration-200",
            "hover:bg-current/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-current",
          )}
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
};

export { Toaster, ToastBody };
